import { X } from "lucide-react";
import {
  forwardRef,
  useId,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "../../utils/classes";
import styles from "./text-field.module.css";

export type TextFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> & {
  label?: ReactNode;
  description?: string;
  error?: string;
  size?: "xs" | "sm" | "md" | "lg";
  leadingIcon?: ReactNode;
  trailingContent?: ReactNode;
  wrapperClassName?: string;
  /** With a controlled value, the consumer clears its state here. */
  onClear?: () => void;
  clearLabel?: string;
};

/** Native input contract; no internal copy of value or mount-time change callback. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      label,
      description,
      error,
      size = "sm",
      leadingIcon,
      trailingContent,
      wrapperClassName,
      onClear,
      clearLabel = "Clear input",
      id,
      className,
      disabled,
      readOnly,
      required,
      value,
      "aria-describedby": describedBy,
      ...props
    },
    ref,
  ) {
    const generatedId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const inputId = id ?? generatedId;
    const descriptionId = `${inputId}-description`;
    const errorId = `${inputId}-error`;
    const descriptions = [
      describedBy,
      description ? descriptionId : undefined,
      error ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(" ");
    const canClear =
      onClear && value !== undefined && value !== "" && !readOnly;

    return (
      <div className={cn(styles.field, wrapperClassName)} data-size={size}>
        {label != null && (
          <label className={styles.label} htmlFor={inputId}>
            {label}
            {required && (
              <span aria-hidden="true" className={styles.required}>
                {" "}
                *
              </span>
            )}
          </label>
        )}
        <div
          className={styles.control}
          data-invalid={Boolean(error)}
          data-disabled={Boolean(disabled)}
        >
          {leadingIcon && (
            <span className={styles.icon} aria-hidden="true">
              {leadingIcon}
            </span>
          )}
          <input
            {...props}
            ref={(node: HTMLInputElement | null) => {
              inputRef.current = node;
              if (typeof ref === "function") return ref(node);
              else if (ref) ref.current = node;
            }}
            id={inputId}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={error ? true : props["aria-invalid"]}
            aria-describedby={descriptions || undefined}
            className={cn(styles.input, className)}
          />
          {trailingContent && (
            <span className={styles.trailing}>{trailingContent}</span>
          )}
          {canClear && (
            <button
              type="button"
              className={styles.clear}
              disabled={disabled}
              aria-label={clearLabel}
              onClick={() => {
                onClear();
                inputRef.current?.focus();
              }}
            >
              <X aria-hidden="true" />
            </button>
          )}
        </div>
        {description && (
          <p id={descriptionId} className={styles.description}>
            {description}
          </p>
        )}
        {error && (
          <p id={errorId} className={styles.error}>
            {error}
          </p>
        )}
      </div>
    );
  },
);
